import type { ComponentProps } from 'react'
import { html, styled } from '@tamagui/tailwind'

// The button tones docs/STYLE.md allows: blue is the only action color, there is no orange CTA.
// 44 px minimum height and a visible focus ring come from the design system's a11y rules.
const frameBase =
  'inline-flex flex-row items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl border text-base font-semibold no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring disabled:opacity-60'

const frameVariants = {
  variants: {
    tone: {
      primary:
        'bg-primary border-primary text-on-primary hover:bg-primary-hover hover:border-primary-hover press:bg-primary-press',
      secondary:
        'bg-surface border-border-color-control text-color hover:bg-background-subtle press:bg-background-muted',
      ghost:
        'bg-transparent border-transparent text-primary-text hover:bg-primary-soft press:bg-primary-soft-hover',
    },
    size: {
      md: 'min-h-[44px] px-4 text-base',
      lg: 'min-h-[52px] px-6 text-base rounded-2xl',
    },
  },
  defaultVariants: {
    tone: 'primary',
    size: 'md',
  },
} as const

const ButtonFrame = styled(html.button, frameBase, frameVariants)

// Same look on an <a>: every landing CTA points at an anchor on the page, and a link has to stay
// a link so middle-click, "open in new tab" and the keyboard behave as people expect.
const ButtonLinkFrame = styled(html.a, frameBase, frameVariants)

// html.button cannot take a bare text child (react-native-web needs a text element), which is why
// the docs' own example wraps the label in html.span. The wrapper does it once, here, and carries
// the tone's text color because the span would not inherit it.
const labelTone = {
  primary: 'text-on-primary',
  secondary: 'text-color',
  ghost: 'text-primary-text',
} as const

export function Button({
  children,
  tone = 'primary',
  ...rest
}: ComponentProps<typeof ButtonFrame> & { tone?: keyof typeof labelTone }) {
  return (
    <ButtonFrame
      tone={tone}
      {...rest}
    >
      <html.span className={`text-base font-semibold ${labelTone[tone]}`}>{children}</html.span>
    </ButtonFrame>
  )
}

export function ButtonLink({
  children,
  tone = 'primary',
  ...rest
}: ComponentProps<typeof ButtonLinkFrame> & { tone?: keyof typeof labelTone }) {
  return (
    <ButtonLinkFrame
      tone={tone}
      {...rest}
    >
      <html.span className={`text-base font-semibold ${labelTone[tone]}`}>{children}</html.span>
    </ButtonLinkFrame>
  )
}

// shortcut: inputs are plain html.input/html.textarea with these class strings instead of
// styled() wrappers, because styled(html.input, ...) types its change event as an HTMLDivElement
// event and the `.value` read stops typechecking. Upgrade when Tamagui types the element through.
export const inputClass =
  'w-full min-h-[44px] px-3 rounded-xl border border-border-color-control bg-surface text-base text-color placeholder:text-color-placeholder focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring'

export const textareaClass =
  'w-full min-h-[120px] p-3 rounded-xl border border-border-color-control bg-surface text-base text-color placeholder:text-color-placeholder focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring'

export const invalidClass = 'border-border-color-danger'

export const field = (invalid?: boolean, textarea?: boolean) =>
  `${textarea ? textareaClass : inputClass}${invalid ? ` ${invalidClass}` : ''}`

// @tamagui/tailwind types every event handler against HTMLDivElement (see its WebEventProps), so
// reading `.value` off a change event needs one cast. It lives here, once, instead of in every
// form field. shortcut: drop these two when Tamagui types handlers per element.
export const inputValue = (event: { currentTarget: unknown }) =>
  (event.currentTarget as HTMLInputElement | HTMLTextAreaElement).value

export const inputChecked = (event: { currentTarget: unknown }) =>
  (event.currentTarget as HTMLInputElement).checked

export const Card = styled(
  html.div,
  'flex flex-col bg-surface border border-border-color rounded-card p-6 max-sm:p-4'
)
